import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useBrand } from '../context/BrandContext';
import { useAuth } from '../context/AuthContext';
import InstallApp from './InstallApp';
import {
  FileText,
  Image,
  Volume2,
  Video,
  LayoutDashboard,
  FolderOpen,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Pencil,
  Crown,
  FlaskConical
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/scripts', label: 'Script Generator', icon: FileText },
  { path: '/images', label: 'Image Generator', icon: Image },
  { path: '/editor', label: 'Image Editor', icon: Pencil },
  { path: '/audio', label: 'Audio Generator', icon: Volume2 },
  { path: '/video', label: 'Video Generator', icon: Video },
  { path: '/library', label: 'Content Library', icon: FolderOpen },
  { path: '/labs', label: 'Student Labs', icon: FlaskConical },
  { path: '/subscription', label: 'Subscription', icon: Crown },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export const Sidebar = () => {
  const { currentBrand, switchBrand, brands } = useBrand();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const SidebarContent = () => (
    <>
      {/* Brand Selector */}
      <div className="p-4 border-b border-zinc-800">
        <div className="relative">
          <button
            data-testid="brand-selector-btn"
            onClick={() => setBrandDropdownOpen(!brandDropdownOpen)}
            className="w-full flex items-center justify-between p-3 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors duration-200"
            style={{ borderColor: `${currentBrand.primary}30` }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                style={{ background: currentBrand.gradient }}
              >
                {currentBrand.name.charAt(0)}
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-zinc-100">{currentBrand.name}</p>
                <p className="text-xs text-zinc-500">{currentBrand.tagline}</p>
              </div>
            </div>
            <ChevronDown 
              className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ${brandDropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          <AnimatePresence>
            {brandDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute top-full left-0 right-0 mt-2 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden z-50 shadow-xl"
              >
                {Object.values(brands).map((brand) => (
                  <button
                    key={brand.id}
                    data-testid={`brand-option-${brand.id}`}
                    onClick={() => {
                      switchBrand(brand.id);
                      setBrandDropdownOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 p-3 hover:bg-zinc-800/50 transition-colors duration-200 ${
                      currentBrand.id === brand.id ? 'bg-zinc-800/30' : ''
                    }`}
                  >
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                      style={{ background: brand.gradient }}
                    >
                      {brand.name.charAt(0)}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-zinc-100">{brand.name}</p>
                      <p className="text-xs text-zinc-500">{brand.tagline}</p>
                    </div>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.path.slice(1)}`}
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                isActive 
                  ? 'text-white' 
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50'
              }`
            }
            style={({ isActive }) => 
              isActive ? { background: `${currentBrand.primary}20`, color: currentBrand.primary } : {}
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-zinc-800">
        {/* Install App CTA */}
        <div className="mb-3">
          <InstallApp variant="sidebar" />
        </div>
        <div className="flex items-center gap-3 mb-4 px-2">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
            style={{ background: currentBrand.gradient }}
          >
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-100 truncate">{user?.name || 'User'}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email || ''}</p>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-400 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Log Out</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        data-testid="mobile-menu-btn"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-xl bg-zinc-900 border border-zinc-800"
      >
        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-72 h-screen bg-zinc-950/80 backdrop-blur-xl border-r border-zinc-800 flex-col fixed left-0 top-0">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/60 z-40"
            />
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="lg:hidden fixed left-0 top-0 w-72 h-screen bg-zinc-950 border-r border-zinc-800 flex flex-col z-50"
            >
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
